import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbChartBar, TbChevronRight } from "solid-icons/tb";
import { createMemo, Show } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { type MediaStorageLocation, useChatRuntime } from "../runtime/ChatRuntime";
import { ProcessingSpinner } from "../ui";
import { sanitizeForFilename } from "../util/download";
import { MediaDownloadButton } from "./MediaDownloadButton";
import { useSignedImageUrl } from "./useSignedImageUrl";

interface ChartPreviewCardProps {
  block: StreamingBlock;
}

interface ChartPreviewData {
  type: "chart";
  htmlStorageLocation?: MediaStorageLocation;
  pngStorageLocation?: MediaStorageLocation;
  metadata?: {
    chartType?: string;
    title?: string;
    height?: number;
  };
}

/**
 * Inline chat-thread preview for chart blocks. Hosts with a viewer open it via
 * `openContentBlock` (bluesky → side panel); hosts with `caps.inlineMedia`
 * (chief) render the chart in place — the interactive HTML loads in a
 * sandboxed iframe straight from its signed URL (a declarative load, like
 * `<img>`), falling back to the PNG snapshot.
 */
export function ChartPreviewCard(props: ChartPreviewCardProps) {
  const rt = useChatRuntime();

  const chartData = createMemo<ChartPreviewData | null>(() => {
    if (props.block.finalContent?.type === "text" && props.block.finalContent.text) {
      const parsed = safeParseBlockJson(props.block.finalContent.text) as ChartPreviewData | null;
      if (parsed?.type === "chart") return parsed;
    }
    const content = getBlockContent(props.block.blockId);
    if (content) {
      const parsed = safeParseBlockJson(content) as ChartPreviewData | null;
      if (parsed?.type === "chart") return parsed;
    }
    return null;
  });

  const isStreaming = () => !props.block.isComplete;
  const metadata = () => chartData()?.metadata;

  const title = createMemo(() => metadata()?.title || rt.t("entity.chart"));
  const subtitle = createMemo(() => {
    const chartType = metadata()?.chartType;
    const label = rt.t("entity.chart");
    return chartType ? `${label} · ${chartType}` : label;
  });

  const canOpen = () => Boolean(rt.actions.openContentBlock);
  const showInline = () =>
    Boolean(rt.actions.resolveImageUrls) &&
    rt.caps.inlineMedia === true &&
    Boolean(chartData()?.htmlStorageLocation || chartData()?.pngStorageLocation);

  const handleClick = () => {
    rt.actions.openContentBlock?.(props.block.blockId);
  };

  return (
    <div data-renderer="chart-preview-card">
      <Show
        when={chartData()}
        fallback={
          <Show when={isStreaming()}>
            <div class="flex items-center gap-3 py-2 text-sm text-secondary-text">
              <ProcessingSpinner class="size-4" />
              <span>{rt.t("generating_chart")}</span>
            </div>
          </Show>
        }
      >
        {(data) => (
          <Show
            when={!showInline()}
            fallback={
              <InlineChart
                html={data().htmlStorageLocation}
                png={data().pngStorageLocation}
                title={title()}
                subtitle={subtitle()}
                height={metadata()?.height || 500}
              />
            }
          >
            <button
              type="button"
              onClick={handleClick}
              disabled={!canOpen()}
              class="flex w-full items-center gap-3 rounded-lg border border-primary-border/50 p-3 text-left transition-colors enabled:cursor-pointer hover:enabled:bg-canvas-hover"
            >
              <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-inset">
                <TbChartBar class="size-5 text-secondary-text" />
              </div>
              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-medium text-primary-text">{title()}</div>
                <div class="mt-0.5 text-xs text-muted-text">{subtitle()}</div>
              </div>
              <Show when={canOpen()}>
                <TbChevronRight class="size-4 shrink-0 text-muted-text" />
              </Show>
            </button>
          </Show>
        )}
      </Show>
    </div>
  );
}

const InlineChart = (props: {
  html?: MediaStorageLocation;
  png?: MediaStorageLocation;
  title: string;
  subtitle: string;
  height: number;
}) => {
  const rt = useChatRuntime();
  const signed = useSignedImageUrl(() => props.html ?? props.png);

  return (
    <div class="overflow-hidden rounded-lg border border-primary-border/50">
      <div class="flex items-start justify-between gap-2 px-3 py-2">
        <div class="min-w-0">
          <div class="truncate text-sm font-medium text-primary-text">{props.title}</div>
          <div class="mt-0.5 text-xs text-muted-text">{props.subtitle}</div>
        </div>
        <Show when={rt.actions.downloadMedia && props.png}>
          {(png) => (
            <MediaDownloadButton
              location={png()}
              filename={`${sanitizeForFilename(props.title).slice(0, 80) || "chart"}.png`}
            />
          )}
        </Show>
      </div>
      <Show when={signed.loading()}>
        <div class="flex items-center justify-center border-t border-primary-border/50 bg-inset p-8">
          <ProcessingSpinner class="size-6" />
        </div>
      </Show>
      <Show when={signed.error()}>
        <div class="flex items-center gap-2 border-t border-primary-border/50 p-3 text-sm text-secondary-text">
          <TbChartBar class="size-4 shrink-0" />
          <span>{rt.t("error_failed_to_load_chart")}</span>
        </div>
      </Show>
      <Show when={signed.url()}>
        {(url) => (
          <Show
            when={props.html}
            fallback={
              <img src={url()} alt={props.title} class="block w-full border-t border-primary-border/50 bg-inset" />
            }
          >
            <iframe
              src={url()}
              sandbox="allow-scripts"
              title={props.title}
              class="block w-full border-0 border-t border-primary-border/50 bg-white"
              style={{ height: `${props.height}px` }}
            />
          </Show>
        )}
      </Show>
    </div>
  );
};
